import { createPersistedStore, storage } from '@connect-x-app/hybrid-utils';
import { FlightRoomSummary, FlightStatus } from '@connect-x-app/shared-domain-models';
import { createSerializableStorage } from '@connect-x-app/shared-utils';

type FlightRoomMap = Map<string, FlightRoomSummary>;

export type FlightRoomState = {
  flightRooms: FlightRoomMap;
};

type FlightRoomActions = {
  removeFlightRoom: (roomId: string) => void;
  resetFlightRooms: () => void;
  setFlightRooms: (rooms: FlightRoomSummary[], status: FlightStatus) => void;
  updateFlightRoom: (room: FlightRoomSummary) => void;
  updatePartialFlightRoom: (roomId: string, partialFlightRoom: Partial<FlightRoomSummary>) => void;
};

type FlightRoomStore = FlightRoomState & FlightRoomActions;

const initialState: FlightRoomState = {
  flightRooms: new Map(),
};

export const useFlightRoomStore = createPersistedStore<FlightRoomStore>(
  (set, _) => ({
    ...initialState,

    removeFlightRoom: roomId =>
      set(state => {
        const newMap = new Map<string, FlightRoomSummary>(state.flightRooms);
        newMap.delete(roomId);

        return { flightRooms: newMap };
      }),

    resetFlightRooms: () => set(() => ({ flightRooms: new Map() })),

    setFlightRooms: (newRooms, status) =>
      set(state => {
        const newMap = new Map([...state.flightRooms].filter(([_, room]) => room.status !== status));
        newRooms.forEach(newRoom => newMap.set(newRoom.id, { ...newRoom, status }));
        return { flightRooms: newMap };
      }),

    updateFlightRoom: room =>
      set(state => {
        const newMap = new Map(state.flightRooms);
        const existingRoom = newMap.get(room.id);

        newMap.set(room.id, {
          ...(existingRoom ?? {}),
          ...room,
        });

        return { flightRooms: newMap };
      }),

    updatePartialFlightRoom: (roomId, partialFlightRoom) =>
      set(state => {
        const newMap = new Map(state.flightRooms);
        const existingRoom = newMap.get(roomId);

        if (existingRoom) {
          newMap.set(roomId, {
            ...existingRoom,
            ...partialFlightRoom,
          });

          return { flightRooms: newMap };
        }

        return {};
      }),
  }),
  {
    name: 'flightrooms-store',
    storage: createSerializableStorage<FlightRoomStore>(storage),
  },
);

export const selectFlightRoomById = (roomId: string): FlightRoomSummary | undefined =>
  useFlightRoomStore.getState().flightRooms.get(roomId);
